import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

const MetaTags = ({ data }) => {
    const siteUrl = process.env.REACT_APP_API_SITE_URL;

    const settings = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('settings')) || {};
        } catch {
            return {};
        }
    }, []);

    if (!data || !settings.site_name) {
        return null; // Guard clause to prevent rendering if data is missing
    }

    const {
        title,
        featured_image,
        excerpt,
        slug,
        modified_time,
        published_time,
        meta_tags,
        post_author_name,
        author_first_name,
        author_last_name,
        author_username,
    } = data;

    const fullUrl = `${siteUrl}/blog/${slug}`;

    return (
        <Helmet>
            {/* Title and Site Info */}
            <title>{`${settings.site_name} | ${title}`}</title>
            <link rel="image_src" href={featured_image} />
            <meta name="author" content={post_author_name} />

            {/* Open Graph */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={excerpt} />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content={settings.site_name} />
            <meta property="og:locale" content="en_US" />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:image" content={featured_image} />
            <meta property="og:image:secure_url" content={featured_image} />
            <meta property="og:image:width" content="1080" />
            <meta property="og:image:height" content="1080" />
            <meta property="og:image:alt" content={title} />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:updated_time" content={modified_time} />

            {/* Article Meta */}
            <meta property="article:tag" content={meta_tags} />
            <meta property="article:published_time" content={published_time} />
            <meta property="article:modified_time" content={modified_time} />
            <meta property="article:author:first_name" content={author_first_name} />
            <meta property="article:author:last_name" content={author_last_name} />
            <meta property="article:author:username" content={author_username} />

            {/* Twitter Meta */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:partner" content="ogwp" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={excerpt} />
            <meta name="twitter:image" content={featured_image} />
            <meta name="twitter:image:alt" content={title} />
            <meta name="twitter:url" content={fullUrl} />

            {/* ItemProp Meta */}
            <meta itemProp="name" content={title} />
            <meta itemProp="description" content={excerpt} />
            <meta itemProp="image" content={featured_image} />
            <meta itemProp="datePublished" content={published_time} />
            <meta itemProp="dateModified" content={modified_time} />
            <meta itemProp="author" content={post_author_name} />

            {/* Profile Meta */}
            <meta property="profile:first_name" content={author_first_name} />
            <meta property="profile:last_name" content={author_last_name} />
            <meta property="profile:username" content={author_username} />

            {/* Windows Tile Image */}
            <meta name="msapplication-TileImage" content={featured_image} />
        </Helmet>
    );
};

MetaTags.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        featured_image: PropTypes.string,
        excerpt: PropTypes.string,
        slug: PropTypes.string,
        modified_time: PropTypes.string,
        published_time: PropTypes.string,
        meta_tags: PropTypes.string,
        post_author_name: PropTypes.string,
        author_first_name: PropTypes.string,
        author_last_name: PropTypes.string,
        author_username: PropTypes.string,
    }).isRequired,
};

export default MetaTags;
